import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { put, call, select, all, fork, cancelled, delay } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { initDeviationPage } from '../../DeviationPage/redux/actions';
import { DeviationResourceType } from '../../types/deviation';
import { request } from '../../xhr/request';
import { getCurrentBiMetadata, getExtendedItem, getItemIdsForPreload, getPreloadingEnabled } from './selectors';
import { getExtendedDeviationById, getDeviationIdFromNormalizedId } from '../../redux/deviations/selectors';
import { getCommentCursorFromQuery } from '../../CommentThread/helpers';
import history from '../../redux/routing/history';
import { isExtendedDeviationFullyLoaded } from '../../DeviationPage/redux/helpers';
import { ConfigSelectors } from '../../config';
import { newPageLoaded, setSafetyState } from '../../Ads/redux/actions';
import { getBiEventData } from '../../DeviationPage/redux/selectors';
import { logPageView } from '../../biLogger/redux/actions';
import { updateHistoryWith } from './saga';
const logger = ddt.createLogger('duperbrowse');
function getDeviationPageTypeFromTypeId(typeId) {
    switch(typeId){
        case DeviationResourceType.JOURNAL:
            return 'journal';
        case DeviationResourceType.COMMISSION:
            return 'commission';
        case DeviationResourceType.POLL:
            return 'poll';
        case DeviationResourceType.STATUS:
            return 'status-update';
        case DeviationResourceType.TIER:
            // TODO member_subscriptions clean up any when backend supports tiers for related content
            return 'tier';
        default:
            return 'art';
    }
}
function* loadDeviation(itemId, preload, currentDeviationId) {
    // check if we already have fetched data for extended
    const deviation = yield select(getExtendedDeviationById, itemId);
    if (!deviation) {
        logger.error('Deviation not found', itemId);
        return false;
    }
    if (isExtendedDeviationFullyLoaded(deviation)) {
        // already loaded, we good
        logger.log('Deviation already loaded, bailing', itemId);
        if (!preload) {
            yield put(setSafetyState('ready'));
        }
        return false;
    }
    logger.log('Loading deviation', itemId, {
        preload,
        currentDeviationId
    });
    try {
        var _result_ads;
        const csrf = yield select(ConfigSelectors.key('csrfToken'));
        const queryParams = new URLSearchParams(history.location.search);
        const parsedQuery = {};
        queryParams.forEach((value, key)=>parsedQuery[key] = value);
        const params = {
            deviationid: deviation.deviationId,
            username: deviation.author && deviation.author.username,
            type: getDeviationPageTypeFromTypeId(deviation.typeId),
            include_session: false,
            comments_cursor: getCommentCursorFromQuery(parsedQuery, {
                itemId: deviation.deviationId
            }),
            csrf_token: csrf
        };
        if (preload) {
            params.preload = true;
            if (currentDeviationId) {
                params.current_id = yield select(getDeviationIdFromNormalizedId, currentDeviationId);
            }
        }
        const result = yield call(request, {
            method: 'get',
            url: 'deviation/extended_fetch',
            params
        }, ()=>{
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line
                const chance = require('chance').Chance();
                // eslint-disable-next-line
                const { Fake, commentThreads } = require('@wix/da-sample-data');
                return {
                    deviation: Fake.extendDeviation(deviation, {}),
                    comments: _object_spread_props(_object_spread({}, chance.pickone(commentThreads)), {
                        commentableItemid: deviation.deviationId,
                        commentableTypeid: deviation.typeId
                    })
                };
            }
            return false;
        });
        if (!result || !result.deviation) {
            return false;
        }
        if (!preload) {
            yield put(setSafetyState('ready'));
        }
        yield put(initDeviationPage(result.deviation, result.comments, ((_result_ads = result.ads) === null || _result_ads === void 0 ? void 0 : _result_ads.unsafe) || 'off', result.pageBiMetadata));
        return true;
    } finally{
        if (yield cancelled()) {
            logger.log('Loading canceled', deviation, {
                preload
            });
        }
    }
}
function getLoader(itemSchema) {
    switch(itemSchema){
        case 'deviation':
            return loadDeviation;
        default:
    }
}
export function* handleLoading({ payload: { itemId, itemSchema, preloadDirection } }) {
    yield delay(200); // debounce
    const loader = getLoader(itemSchema);
    if (!loader) {
        logger.log('No loader found', itemSchema);
        return;
    }
    logger.log('Starting main load', itemId);
    // this is where we load the current item synchronously
    const wasRealLoad = yield call(loader, itemId, false);
    const currentItem = wasRealLoad ? undefined : itemId; // we only should pass currentItem for preloaded deviations
    if (itemSchema === 'deviation') {
        const devItem = yield select(getExtendedItem, itemId, itemSchema);
        yield call(updateHistoryWith, devItem, true);
        logger.log('Update url for', itemId, devItem);
        // We track BI here as we want a "full" pageload before sending the event
        // and this handler best suits that logic
        const deviation = yield select(getExtendedDeviationById, itemId);
        const biMetadata = yield select(getCurrentBiMetadata, itemId);
        const { view, eventComponent, deviationId, deviationResourceType, isDuperbrowse, contentViewStatus } = yield select(getBiEventData, deviation);
        yield put(logPageView({
            view,
            component: eventComponent,
            typeid: deviationResourceType,
            itemid: deviationId,
            is_duper: isDuperbrowse ? 1 : 0,
            content_view_status: contentViewStatus,
            useNg: true,
            metadata: biMetadata
        }));
    }
    // Freestar page change tracking
    yield put(newPageLoaded());
    const preloadEnabled = yield select(getPreloadingEnabled);
    if (preloadEnabled) {
        const navigation = yield select(getItemIdsForPreload, preloadDirection);
        const preloadEffects = navigation.map((item)=>fork(loader, item, true, currentItem));
        logger.log('Forking preloads', navigation);
        // here we fork out the preloaders to do their thing async
        yield all(preloadEffects);
    } else {
        logger.log('No preloading for this stream');
    }
}
